import classNames from 'classnames';
import React, { InputHTMLAttributes, useRef, ChangeEventHandler, useCallback } from 'react';
import styled from '@emotion/styled';
import { colors, border_radius_styles_with_size, semantic_colors } from '../../foundation';
import { hidden, CSSValueWithLength, buildCSSWithLength } from '../../styles';
import { IconLoadingDonuts, IconPlus } from '../icons/generated';
import { useProviderConfig } from '../provider';
import { normalizeImageAccept } from '../../utils/imageAccept';

export interface ImageUploadButtonProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'accept'> {
  /** @default 'medium' */
  size?: 'medium' | 'small';
  width?: number;
  height?: number;
  /** @default false */
  multiple?: boolean;
  loading?: boolean;
  /** @default border_radius_styles_with_size.small */
  borderRadius?: CSSValueWithLength;
  onFileUpload?: (file: File | File[]) => void;
  /**
   * 허용되는 이미지 확장자를 지정합니다.
   *
   * @example
   * accept="*" // 모든 이미지 타입, 기본값
   * accept="png"
   * accept="png, jpg, gif"
   * accept=".png, .jpg, .gif"
   * accept="image/png, image/jpg"
   *
   * @default '*'
   */
  accept?: string;
}

export const ImageUploadButton = ({
  size = 'medium',
  accept = '*',
  width,
  height,
  readOnly,
  disabled,
  className,
  multiple = false,
  loading,
  borderRadius = border_radius_styles_with_size.small,
  onFileUpload,
  ...props
}: ImageUploadButtonProps) => {
  const { locale } = useProviderConfig();
  const uploadRef = useRef<HTMLInputElement | null>(null);

  const handleClickUpload = () => {
    if (!uploadRef.current || loading) return;
    uploadRef.current.click();
  };

  const handleFileChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = multiple ? Array.from(event.target.files) : event.target.files[0];
        event.target.value = '';
        onFileUpload?.(file);
      }
    },
    [onFileUpload, multiple],
  );

  return (
    <>
      <label hidden htmlFor={props.id}>
        <HiddenInput
          type='file'
          accept={normalizeImageAccept(accept)}
          disabled={disabled || readOnly}
          multiple={multiple}
          className={classNames('pds-button', className)}
          onChange={handleFileChange}
          {...props}
          ref={uploadRef}
        />
      </label>
      <UploadButton
        size={size}
        width={width}
        height={height}
        disabled={disabled || readOnly}
        loading={loading}
        onClick={handleClickUpload}
        type='button'
        aria-label={locale?.ImageUpload.label}
        borderRadius={borderRadius}
      >
        {loading ? <IconLoadingDonuts size={24} /> : <IconPlus size={24} color='currentColor' />}
      </UploadButton>
    </>
  );
};

const HiddenInput = styled.input`
  ${hidden};
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
const UploadButton = styled.button<{
  size: 'medium' | 'small';
  width?: number;
  height?: number;
  disabled?: boolean;
  loading?: boolean;
  borderRadius: CSSValueWithLength;
}>`
  display: flex;
  ${({ size, width, height }) =>
    size === 'medium'
      ? `
    width: ${width ? Math.max(width, 60) : 100}px;
    height: ${height ? Math.max(height, 60) : 100}px;
  `
      : `
    width: 40px;
    height: 40px;
  `}
  align-items: center;
  justify-content: center;
  ${({ borderRadius }) => buildCSSWithLength('border-radius', borderRadius)}
  border: 1px dashed ${semantic_colors.border.secondary};
  background-color: ${semantic_colors.fill.tertiary};
  color: ${semantic_colors.content.primary};
  transition: border-color 0.1s linear, background-color 0.1s linear;
  cursor: pointer;

  &:hover {
    color: ${semantic_colors.accent.primary};
    border-color: ${semantic_colors.accent.primary};
    background-color: ${colors.blue50};
  }
  &:active {
    color: ${semantic_colors.accent.primary};
    border-color: ${semantic_colors.accent.primary};
    background-color: ${colors.blue100};
  }
  ${({ loading }) =>
    loading &&
    `
      &, &:hover, &:active {
        border: 1px dashed ${semantic_colors.border.primary};
        background-color: ${colors.gray10};
        cursor: progress;
      }
    `}

  ${({ disabled }) =>
    disabled &&
    `
      &, &:hover, &:active {
        border-color: ${semantic_colors.border.disabled};
        background-color: ${semantic_colors.background.disabled};
        color: ${semantic_colors.content.disabled};
        cursor: not-allowed;
      }
    `}
`;
